import styled from "@emotion/styled";
import { Typography, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useBreakpoint } from "@avenue-8/sidekick-ui";
import { useUIStore } from "@store/ui.store";
import { ExpandSidebarButton } from "@shared/components/ExpandSidebarButton";

const HeaderBar = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 4rem;
  box-shadow: ${(p) => p.theme.shadows.primary};
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};
  background-color: ${(p) => p.theme.colors.background};
  flex-shrink: 0;
  box-sizing: border-box;

  ${(p) => p.theme.media.mobileDown} {
    min-height: 4.5rem;
  }
`;

const SideSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 4rem;
  flex-shrink: 0;
`;

const TitleBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  justify-content: center;
`;

const Title = styled(Typography)`
  font-weight: 600;
  font-size: ${(p) => p.theme.text.fontSizes.l};
  line-height: 1.2;
  color: ${(p) => p.theme.text.colors.brand};
`;

const Subtitle = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.m};
  color: ${(p) => p.theme.text.colors.brand};
  line-height: 1.2;
`;

type PageNavbarProps = {
  title: string;
  subtitle?: string;
  onBack?: () => void;
  rightElement?: React.ReactNode;
};

export function PageNavbar(props: PageNavbarProps) {
  const { title, subtitle, onBack, rightElement } = props;

  const isLaptopDown = useBreakpoint("laptop");
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const isSidebarOpen = useUIStore((state) => state.isSidebarOpen);

  return (
    <HeaderBar>
      <SideSlot>
        {onBack ? (
          <IconButton
            onClick={onBack}
            aria-label="Back"
            color="inherit"
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
        ) : (
          isLaptopDown && (
            <ExpandSidebarButton
              onClick={() => toggleSidebar(true)}
              hide={isSidebarOpen}
            />
          )
        )}
      </SideSlot>
      <TitleBlock>
        <Title variant="h6">{title}</Title>
        {subtitle != null && subtitle !== "" && (
          <Subtitle variant="body2">{subtitle}</Subtitle>
        )}
      </TitleBlock>
      <SideSlot>{rightElement}</SideSlot>
    </HeaderBar>
  );
}
